import { useForm, useWatch } from 'react-hook-form'
import {
  ControlledInput,
  ControlledRadioGroupInput,
  ControlledTextarea,
  ControlledToggle,
  Button,
  ButtonLink,
  Title,
  ControlledNumberInput,
  ControlledDateInput,
} from '@/components'
import { Fragment, useEffect, useRef, useState } from 'react'
import { Tab } from '@headlessui/react'
import dayjs from 'dayjs'
import { ReactSVG } from 'react-svg'
import formatAddress from '@/lib/formatAddress'
import { AiOutlineCar } from 'react-icons/ai'
import { GiStreetLight, GiUndergroundCave } from 'react-icons/gi'
import { MdHighlightOff, MdGarage, MdApartment, MdLocalLaundryService } from 'react-icons/md'
import { RiParkingBoxLine } from 'react-icons/ri'
import clsx from 'clsx'
import { ImageUploader } from '../Images'
import {
  CalendarIcon,
  CashIcon,
  ChatIcon,
  InformationCircleIcon,
  PhotographIcon,
  XCircleIcon,
} from '@heroicons/react/outline'

const yearFromNow = new Date(dayjs(new Date()).add(1, 'year').format())

interface AddressStepProps {
  handleUpdate: (data: any) => void
  handleBack?: () => void
  canGoBack?: boolean
  data?: any
}

export const RentalContactStep = ({ handleUpdate, handleBack, canGoBack = true, data }: AddressStepProps) => {
  const [images, setImages] = useState([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const el = useRef<HTMLDivElement>()
  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
  } = useForm<any>({
    defaultValues: {
      ...data,
    },
  })

  useEffect(() => {
    if (data.images) {
      setImages(data.images)
    }
  }, [data])

  const handleImages = (uploadedImages: []) => {
    setImages(uploadedImages)
  }

  const handleSubmitFn = (data) => {
    console.log(selectedIndex)
    const errorLoad = []
    if (!data.email) {
      errorLoad.push({ name: 'email', type: 'required', message: 'Email' })
    }

    if (!data.phoneNumber) {
      errorLoad.push({ name: 'phoneNumber', type: 'required', message: 'Phone Number' })
    }

    if (!data.description) {
      errorLoad.push({ name: 'description', type: 'required', message: 'Description' })
    }

    if (!data.bedrooms) {
      errorLoad.push({ name: 'bedrooms', type: 'required', message: 'Bedrooms' })
    }

    if (!data.bathrooms) {
      errorLoad.push({ name: 'bathrooms', type: 'required', message: 'Bedrooms' })
    }

    if (!data.availability) {
      errorLoad.push({ name: 'availability', type: 'required', message: 'Availability date' })
    }

    if (!data.rentPrice) {
      errorLoad.push({ name: 'rentPrice', type: 'required', message: 'Rent price' })
    }

    if (errorLoad.length) {
      errorLoad.forEach(({ name, type, message }) => setError(name, { type, message }))
    } else {
      handleUpdate({ ...data, images })
    }
  }

  const handleChange = (val) => {
    setSelectedIndex(val)
    if (el.current) {
      el.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }

  const gasIncluded = useWatch({
    control,
    name: 'gasIncluded',
  })
  const hydroIncluded = useWatch({
    control,
    name: 'hydroIncluded',
  })
  const waterIncluded = useWatch({
    control,
    name: 'waterIncluded',
  })
  const parkingLocation = useWatch({
    control,
    name: 'parkingLocation',
  })
  const laundryLocation = useWatch({
    control,
    name: 'laundryLocation',
  })
  const laundryIncluded = useWatch({
    control,
    name: 'laundryIncluded',
  })
  const dryerIncluded = useWatch({
    control,
    name: 'dryerIncluded',
  })

  return (
    <>
      <header className="pt-4 pb-4 scroll-mt-36" ref={el}>
        <Title as="p" size="lg">
          {formatAddress(data)}
        </Title>
      </header>
      <div className="">
        <Tab.Group selectedIndex={selectedIndex} onChange={handleChange}>
          <div className="block sticky top-20 z-30 border-b dark:border-zinc-800 border-zinc-200 dark:bg-hunter bg-sand-400">
            <Tab.List className="flex">
              <Tab as={Fragment}>
                {({ selected }) => (
                  <button
                    type="button"
                    className={clsx(
                      selected
                        ? 'text-brand-500 dark:text-zinc-50 border-brand-500'
                        : 'dark:text-zinc-400 hover:dark:text-zinc-50 text-zinc-600 hover:text-brand-600 hover:bg-zinc-200 hover:bg-opacity-50 hover:dark:bg-zinc-800 border-transparent hover:border-brand',
                      'group  px-6 py-4 flex items-center text-sm font-semibold justify-center relative border-b-2  focus:outline-brand ',
                    )}
                  >
                    <ChatIcon
                      className={clsx(
                        selected
                          ? 'dark:text-brand-200 text-brand-500'
                          : 'dark:text-brand-400 text-zinc-500 group-hover:text-brand-500 dark:group-hover:text-brand-300',
                        'flex-shrink-0 sm:-ml-1 sm:mr-3 h-6 w-6',
                      )}
                    />
                    <span className="sr-only lg:not-sr-only">Contact</span>
                    <span
                      className={clsx(
                        selected ? 'block after:-bottom-[2px]' : 'hidden after:bottom-0',
                        'after:h-1 after:w-full after:left-0 after:content-[""] after:absolute  after:bg-gradient-to-r  after:from-brand-500  after:to-brand-500 after:dark:to-brand-500  -z-[1] after:pointer-events-none after:rounded-lg',
                      )}
                    />
                  </button>
                )}
              </Tab>
              <Tab as={Fragment}>
                {({ selected }) => (
                  <button
                    type="button"
                    className={clsx(
                      selected
                        ? 'text-brand-500 dark:text-zinc-50 border-brand-500'
                        : 'dark:text-zinc-400 hover:dark:text-zinc-50 text-zinc-600 hover:text-brand-600 hover:bg-zinc-200 hover:bg-opacity-50 hover:dark:bg-zinc-800 border-transparent hover:border-brand',
                      'group  px-6 py-4 flex items-center text-sm font-semibold justify-center relative border-b-2  focus:outline-brand ',
                    )}
                  >
                    <CalendarIcon
                      className={clsx(
                        selected
                          ? 'dark:text-brand-500 text-brand-500'
                          : 'dark:text-brand-400 text-brand-400 group-hover:text-brand-500 dark:group-hover:text-brand-300',
                        'flex-shrink-0 sm:-ml-1 sm:mr-3 h-6 w-6',
                      )}
                    />
                    <span className="sr-only lg:not-sr-only">Details</span>
                    <span
                      className={clsx(
                        selected ? 'block after:-bottom-[2px]' : 'hidden after:bottom-0',
                        'after:h-1 after:w-full after:left-0 after:content-[""] after:absolute  after:bg-gradient-to-r  after:from-brand-500  after:to-brand-500 after:dark:to-brand-500  -z-[1] after:pointer-events-none after:rounded-lg',
                      )}
                    />
                  </button>
                )}
              </Tab>
              <Tab as={Fragment}>
                {({ selected }) => (
                  <button
                    type="button"
                    className={clsx(
                      selected
                        ? 'text-brand-500 dark:text-zinc-50 border-brand-500'
                        : 'dark:text-zinc-400 hover:dark:text-zinc-50 text-zinc-600 hover:text-brand-600 hover:bg-zinc-200 hover:bg-opacity-50 hover:dark:bg-zinc-800 border-transparent hover:border-brand',
                      'group  px-6 py-4 flex items-center text-sm font-semibold justify-center relative border-b-2  focus:outline-brand ',
                    )}
                  >
                    <CashIcon
                      className={clsx(
                        selected
                          ? 'dark:text-brand-500 text-brand-500'
                          : 'dark:text-brand-400 text-brand-400 group-hover:text-brand-500 dark:group-hover:text-brand-300',
                        'flex-shrink-0 sm:-ml-1 sm:mr-3 h-6 w-6',
                      )}
                    />
                    <span className="sr-only lg:not-sr-only">Prices</span>
                    <span
                      className={clsx(
                        selected ? 'block after:-bottom-[2px]' : 'hidden after:bottom-0',
                        'after:h-1 after:w-full after:left-0 after:content-[""] after:absolute  after:bg-gradient-to-r  after:from-brand-500  after:to-brand-500 after:dark:to-brand-500  -z-[1] after:pointer-events-none after:rounded-lg',
                      )}
                    />
                  </button>
                )}
              </Tab>

              <Tab as={Fragment}>
                {({ selected }) => (
                  <button
                    type="button"
                    className={clsx(
                      selected
                        ? 'text-brand-500 dark:text-zinc-50 border-brand-500'
                        : 'dark:text-zinc-400 hover:dark:text-zinc-50 text-zinc-600 hover:text-brand-600 hover:bg-zinc-200 hover:bg-opacity-50 hover:dark:bg-zinc-800 border-transparent hover:border-brand',
                      'group  px-6 py-4 flex items-center text-sm font-semibold justify-center relative border-b-2  focus:outline-brand ',
                    )}
                  >
                    <InformationCircleIcon
                      className={clsx(
                        selected
                          ? 'dark:text-brand-500 text-brand-500'
                          : 'dark:text-brand-400 text-brand-400 group-hover:text-brand-500 dark:group-hover:text-brand-300',
                        'flex-shrink-0 sm:-ml-1 sm:mr-3 h-6 w-6',
                      )}
                    />
                    <span className="sr-only lg:not-sr-only">Info</span>
                    <span
                      className={clsx(
                        selected ? 'block after:-bottom-[2px]' : 'hidden after:bottom-0',
                        'after:h-1 after:w-full after:left-0 after:content-[""] after:absolute  after:bg-gradient-to-r  after:from-brand-500  after:to-brand-500 after:dark:to-brand-500  -z-[1] after:pointer-events-none after:rounded-lg',
                      )}
                    />
                  </button>
                )}
              </Tab>
              <Tab as={Fragment}>
                {({ selected }) => (
                  <button
                    type="button"
                    className={clsx(
                      selected
                        ? 'text-brand-500 dark:text-zinc-50 border-brand-500'
                        : 'dark:text-zinc-400 hover:dark:text-zinc-50 text-zinc-600 hover:text-brand-600 hover:bg-zinc-200 hover:bg-opacity-50 hover:dark:bg-zinc-800 border-transparent hover:border-brand',
                      'group  px-6 py-4 flex items-center text-sm font-semibold justify-center relative border-b-2  focus:outline-brand ',
                    )}
                  >
                    <PhotographIcon
                      className={clsx(
                        selected
                          ? 'dark:text-brand-500 text-brand-500'
                          : 'dark:text-brand-400 text-brand-400 group-hover:text-brand-500 dark:group-hover:text-brand-300',
                        'flex-shrink-0 sm:-ml-1 sm:mr-3 h-6 w-6',
                      )}
                    />
                    <span className="sr-only lg:not-sr-only">Images</span>
                    <span
                      className={clsx(
                        selected ? 'block after:-bottom-[2px]' : 'hidden after:bottom-0',
                        'after:h-1 after:w-full after:left-0 after:content-[""] after:absolute  after:bg-gradient-to-r  after:from-brand-500  after:to-brand-500 after:dark:to-brand-500  -z-[1] after:pointer-events-none after:rounded-lg',
                      )}
                    />
                  </button>
                )}
              </Tab>
            </Tab.List>
          </div>
          <form onSubmit={handleSubmit(handleSubmitFn)} className="w-full flex-1 px-0 pt-8 sm:pb-6">
            <Tab.Panels>
              <Tab.Panel className="pt-3">
                <div className="mb-6">
                  <Title as="h3" size="xl">
                    How should renters contact you?
                  </Title>
                  <p className="mt-2 max-w-2xl text-sm dark:text-zinc-200 text-zinc-600 font-medium">
                    This information will be available publicly so be careful what you share.
                  </p>
                </div>
                <div className="mb-8">
                  <ControlledInput
                    name="email"
                    type="email"
                    control={control}
                    rules={{ required: true }}
                    label="Your Email Address"
                    className="w-full sm:w-1/2"
                    placeholder="you@internet.com"
                  />
                  <div className="mt-6">
                    <ControlledToggle
                      control={control}
                      name="canEmail"
                      label="Would you like interested renters to contact you via email?"
                      reverse
                    />
                  </div>
                </div>

                <div className="py-8 border-t border-zinc-300  dark:border-zinc-700  space-y-8 md:space-y-6 gap-6">
                  <ControlledInput
                    name="phoneNumber"
                    type="tel"
                    pattern="[0-9\-\(\)\+\s]+"
                    control={control}
                    rules={{ required: true }}
                    label="Your Phone Number"
                    placeholder="555-867-5309"
                    className="w-full sm:w-1/2"
                  />
                  <div className="">
                    <ControlledToggle
                      control={control}
                      name="canPhone"
                      label="Would you like interested renters to contact you via telephone?"
                      reverse
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-16">
                  <Button outline small type="button" onClick={() => handleChange(selectedIndex + 1)}>
                    Next
                  </Button>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3">
                <div className="mb-6 ">
                  <Title as="h3" size="xl">
                    Rental Details
                  </Title>
                  <p className="mt-2 max-w-2xl text-sm dark:text-zinc-200 text-zinc-600 font-medium">
                    The more information, the better.
                  </p>
                </div>

                <div className="mt-6 pb-16">
                  <ControlledTextarea
                    name="description"
                    control={control}
                    rules={{ required: true }}
                    label="Description"
                  />
                </div>
                <div className="mb-6">
                  <Title as="h3" size="lg">
                    Availability
                  </Title>
                  <p className="mt-2 max-w-2xl text-sm dark:text-zinc-200 text-zinc-600 font-medium">
                    When can tenants move in?
                  </p>
                </div>
                <div className="h-full">
                  <ControlledDateInput
                    label="Availability Date"
                    name="availability"
                    control={control}
                    minDate={new Date()}
                    maxDate={yearFromNow}
                    className="w-1/2"
                  />
                </div>
                <div className="mt-16 pb-8">
                  <ControlledRadioGroupInput
                    control={control}
                    name="bedrooms"
                    label="Number of Bedrooms"
                    rules={{ required: true }}
                    options={[
                      { name: 0, label: 'Bachelor', description: '' },
                      { name: 1, label: '1', description: '' },
                      { name: 2, label: '2', description: '' },
                      { name: 3, label: '3', description: '' },
                      { name: '3+', label: '3+', description: '' },
                    ]}
                  />
                </div>

                <div className="py-6">
                  <ControlledRadioGroupInput
                    control={control}
                    name="bathrooms"
                    label="Number of Bathrooms"
                    rules={{ required: true }}
                    options={[
                      { name: 0, label: '0', description: '' },
                      { name: 1, label: '1', description: '' },
                      { name: 2, label: '2', description: '' },
                      { name: 3, label: '3', description: '' },
                      { name: '3+', label: '3+', description: '' },
                    ]}
                  />
                </div>
                <div className="pt-16 pb-6  space-x-6 flex items-center">
                  <div className="rounded-full p-2.5 bg-green-200 dark:shadow-subtle shadow-subtler">
                    <ReactSVG
                      src="/icons/dishwasher.svg"
                      title="Dishwasher Icon"
                      aria-hidden
                      className="stroke-green-800 h-8 w-8"
                    />
                  </div>
                  <ControlledToggle
                    control={control}
                    name="dishwasherAvailable"
                    label="Is a dishwasher available in the unit?"
                    stacked
                  />
                </div>
                <div className="py-6 space-x-6 flex items-center">
                  <div className="rounded-full p-3 bg-brand-50  dark:shadow-subtle shadow-subtler">
                    <ReactSVG
                      src="/icons/airconditioner.svg"
                      title="Good Neighbour"
                      className="stroke-brand-800 h-8 w-8"
                    />
                  </div>
                  <ControlledToggle
                    control={control}
                    name="airConditioningIncluded"
                    label="Does the unit have air conditioning?"
                    stacked
                  />
                </div>
                <div className="py-6  space-x-6 flex items-center">
                  <div className="rounded-full p-3 bg-amber-200  dark:shadow-subtle shadow-subtler">
                    <ReactSVG src="/icons/storage.svg" title="Good Neighbour" className="stroke-amber-800 h-8 w-8" />
                  </div>
                  <ControlledToggle
                    control={control}
                    name="storageAvailable"
                    label="Is there storage available?"
                    stacked
                  />
                </div>
                <div className="py-6  space-x-6 flex items-center">
                  <div className="rounded-full p-3 bg-orange-200  dark:shadow-subtle shadow-subtler">
                    <ReactSVG
                      src="/icons/controlled_access.svg"
                      title="Good Neighbour"
                      className="stroke-orange-800 h-8 w-8"
                    />
                  </div>
                  <ControlledToggle
                    control={control}
                    name="controlledAccess"
                    label="Is there controlled access included?"
                    description="A concierge or buzzer for example"
                    stacked
                  />
                </div>

                <div className="py-6  space-x-6 flex items-center">
                  <div className="rounded-full p-3 bg-rose-200  dark:shadow-subtle shadow-subtler">
                    <ReactSVG
                      src="/icons/outdoor_space.svg"
                      title="Good Neighbour"
                      className="stroke-rose-800 h-8 w-8"
                    />
                  </div>
                  <ControlledToggle control={control} name="outdoorSpace" label="Is outdoor space available?" stacked />
                </div>
                <div className="py-6 space-x-6 flex items-center">
                  <div className="rounded-full p-3 bg-emerald-200  dark:shadow-subtle shadow-subtler">
                    <ReactSVG src="/icons/balcony.svg" title="Good Neighbour" className="stroke-emerald-800 h-8 w-8" />
                  </div>
                  <ControlledToggle control={control} name="balcony" label="Is a balcony available?" stacked />
                </div>
                <div className="flex justify-end mt-6">
                  <Button outline small type="button" onClick={() => handleChange(selectedIndex + 1)}>
                    Next
                  </Button>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3">
                <div className="mb-6">
                  <Title as="h3" size="xl">
                    Prices
                  </Title>
                </div>

                <div className="w-full sm:w-64">
                  <ControlledNumberInput
                    label="Monthly Rent Price"
                    control={control}
                    name="rentPrice"
                    min={100}
                    max={10000}
                    rules={{ required: true }}
                    required
                    toFixedValue={2}
                    displayFormat="$"
                    displayPrice
                    defaultValue={100}
                  />
                </div>
                <div className="mt-16 mb-6">
                  <Title as="h3" size="lg">
                    Utilities
                  </Title>
                  <p className="mt-2 max-w-2xl text-sm dark:text-zinc-200 text-zinc-600 font-medium">
                    This information will be displayed publicly so be careful what you share.
                  </p>
                </div>

                <div className="pb-4 lg:pb-0 mb-6  sm:space-y-0 flex flex-col md:flex-row flex-wrap items-start">
                  <div className="flex items-center gap-6 mb-6">
                    <div className="inline-block rounded-full p-2.5 bg-orange-200 dark:shadow-subtle shadow-subtler">
                      <ReactSVG src="/icons/gas.svg" title="gas" className="stroke-orange-800 h-8 w-8" />
                    </div>
                    <ControlledToggle
                      className="w-64"
                      control={control}
                      name="gasIncluded"
                      label="Is gas included?"
                      stacked
                    />
                  </div>

                  <ControlledNumberInput
                    label="Average monthly gas bill"
                    control={control}
                    name="gasAvgPrice"
                    min={100}
                    max={10000}
                    toFixedValue={2}
                    displayFormat="$"
                    displayPrice
                    defaultValue={50}
                    className="w-full sm:w-64"
                    disabled={gasIncluded}
                  />
                </div>

                <div className="pb-6 mb-6 sm:pb-0 sm:mb-0 sm:space-y-0 flex flex-col md:flex-row flex-wrap items-start pt-8 mt-6 border-t border-zinc-300  dark:border-zinc-700 ">
                  <div className="flex items-center gap-6 mb-6">
                    <div className="rounded-full p-2.5 bg-red-200 dark:shadow-subtle shadow-subtler">
                      <ReactSVG src="/icons/hydro.svg" title="hydro" className="stroke-red-800 h-8 w-8" />
                    </div>
                    <ControlledToggle
                      className="w-64"
                      control={control}
                      name="hydroIncluded"
                      label="Is electricity included?"
                      stacked
                    />
                  </div>
                  <ControlledNumberInput
                    label="Average monthly electricity bill"
                    control={control}
                    name="hydroAvgPrice"
                    min={100}
                    max={10000}
                    toFixedValue={2}
                    displayFormat="$"
                    displayPrice
                    defaultValue={50}
                    className="w-full sm:w-64"
                    disabled={hydroIncluded}
                  />
                </div>

                <div className="pb-6 mb-6 sm:pb-0 sm:mb-0 sm:space-y-0 flex flex-col md:flex-row flex-wrap items-start pt-8 mt-6 border-t border-zinc-300  dark:border-zinc-700 ">
                  <div className="flex items-center gap-6 mb-6">
                    <div className="rounded-full p-2.5 bg-cyan-200 dark:shadow-subtle shadow-subtler">
                      <ReactSVG src="/icons/water.svg" title="water" className="stroke-cyan-800 h-8 w-8" />
                    </div>
                    <ControlledToggle
                      className="w-64"
                      control={control}
                      name="waterIncluded"
                      label="Is water included?"
                      stacked
                    />
                  </div>
                  <ControlledNumberInput
                    label="Average monthly water bill"
                    control={control}
                    name="waterAvgPrice"
                    min={100}
                    max={10000}
                    toFixedValue={2}
                    displayFormat="$"
                    displayPrice
                    defaultValue={50}
                    className="w-full sm:w-64"
                    disabled={waterIncluded}
                  />
                </div>
                <div className="mt-16 mb-6">
                  <Title as="h3" size="md">
                    Parking
                  </Title>
                </div>
                <div className="pb-6">
                  <ControlledRadioGroupInput
                    control={control}
                    name="parkingLocation"
                    label="Parking location"
                    options={[
                      {
                        name: 'none',
                        label: 'None',
                        description: '',
                        icon: <MdHighlightOff className="w-5 h-5 stroke-brand-500 fill-brand-500" />,
                      },
                      {
                        name: 'garage',
                        label: 'Garage',
                        description: '',
                        icon: (
                          <MdGarage className="w-5 h-5 dark:stroke-brand-500 dark:fill-brand-500 stroke-brand-500 fill-brand-500" />
                        ),
                      },
                      {
                        name: 'driveway',
                        label: 'Driveway',
                        description: '',
                        icon: (
                          <AiOutlineCar className="w-5 h-5 dark:stroke-brand-500 dark:fill-brand-500 stroke-brand-500 fill-brand-500" />
                        ),
                      },
                      {
                        name: 'lot',
                        label: 'Lot',
                        description: '',
                        icon: (
                          <RiParkingBoxLine className="w-5 h-5 dark:stroke-brand-500 dark:fill-brand-500 stroke-brand-500 fill-brand-500" />
                        ),
                      },
                      {
                        name: 'street',
                        label: 'Street',
                        description: '',
                        icon: (
                          <GiStreetLight className="w-5 h-5 dark:stroke-brand-500 dark:fill-brand-500 stroke-brand-500 fill-brand-500" />
                        ),
                      },
                      {
                        name: 'underground',
                        label: 'Underground',
                        description: '',
                        icon: (
                          <GiUndergroundCave className="w-5 h-5 dark:stroke-brand-500 dark:fill-brand-500 stroke-brand-500 fill-brand-500" />
                        ),
                      },
                    ]}
                  />
                  <div className="pt-6">
                    <ControlledNumberInput
                      label="Parking Price"
                      control={control}
                      name="parkingAvgPrice"
                      min={100}
                      max={10000}
                      toFixedValue={2}
                      displayFormat="$"
                      displayPrice
                      defaultValue={10}
                      className="w-full sm:w-64"
                      disabled={parkingLocation === 'none'}
                    />
                  </div>
                </div>
                <div className="mt-16 mb-6  ">
                  <Title as="h3" size="lg">
                    Laundry
                  </Title>
                  <p className="mt-2 max-w-2xl text-sm dark:text-zinc-200 text-zinc-600 font-medium">
                    This information will be displayed publicly so be careful what you share.
                  </p>
                </div>

                <div className="pb-6">
                  <ControlledRadioGroupInput
                    control={control}
                    name="laundryLocation"
                    label="Laundry location"
                    options={[
                      {
                        name: 'none',
                        label: 'None',
                        description: '',
                        icon: (
                          <MdHighlightOff className="w-5 h-5 dark:stroke-brand-500 dark:fill-brand-500 stroke-brand-500 fill-brand-500" />
                        ),
                      },
                      {
                        name: 'in-unit',
                        label: 'In unit',
                        description: '',
                        icon: (
                          <MdApartment className="w-5 h-5 dark:stroke-brand-500 dark:fill-brand-500 stroke-brand-500 fill-brand-500" />
                        ),
                      },
                      {
                        name: 'basement',
                        label: 'Basement',
                        description: '',
                        icon: (
                          <GiUndergroundCave className="w-5 h-5 dark:stroke-brand-500 dark:fill-brand-500 stroke-brand-500 fill-brand-500" />
                        ),
                      },
                      {
                        name: 'laundromat',
                        label: 'Laundromat',
                        description: '',
                        icon: (
                          <MdLocalLaundryService className="w-5 h-5 dark:stroke-brand-500 dark:fill-brand-500 stroke-brand-500 fill-brand-500" />
                        ),
                      },
                    ]}
                  />
                  <div
                    className={clsx(
                      'space-y-6 sm:space-y-0  sm:items-start sm:space-x-6 mt-6 pt-6',
                      laundryLocation === 'none' ? 'hidden' : 'sm:flex',
                    )}
                  >
                    <ControlledToggle
                      className="w-64"
                      control={control}
                      name="laundryIncluded"
                      label="Is the washing machine free?"
                      stacked
                    />

                    <ControlledNumberInput
                      label="Average washing machine price per load"
                      control={control}
                      name="laundryAvgPrice"
                      min={100}
                      max={10000}
                      toFixedValue={2}
                      displayFormat="$"
                      displayPrice
                      defaultValue={0.25}
                      className="w-full sm:w-64"
                      disabled={laundryIncluded}
                    />
                  </div>
                  <div
                    className={clsx(
                      'space-y-6 sm:space-y-0 sm:items-start sm:space-x-6 mt-6 pt-6 border-t border-zinc-300  dark:border-zinc-700 ',
                      laundryLocation === 'none' ? 'hidden' : 'sm:flex',
                    )}
                  >
                    <ControlledToggle
                      className="w-64"
                      control={control}
                      name="dryerIncluded"
                      label="Is the dryer free?"
                      stacked
                    />

                    <ControlledNumberInput
                      label="Average dryer price per load"
                      control={control}
                      name="dryerAvgPrice"
                      min={100}
                      max={10000}
                      toFixedValue={2}
                      displayFormat="$"
                      displayPrice
                      defaultValue={0.25}
                      className="w-full sm:w-64"
                      disabled={dryerIncluded}
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-6">
                  <Button outline small type="button" onClick={() => handleChange(selectedIndex + 1)}>
                    Next
                  </Button>
                </div>
              </Tab.Panel>

              <Tab.Panel className="pt-3">
                <div className="mb-6">
                  <Title as="h3" size="xl">
                    Experience information
                  </Title>
                  <p className="mt-2 max-w-2xl text-sm dark:text-zinc-200 text-zinc-600 font-medium">
                    Help us provide more information to tenants
                  </p>
                </div>
                <div className="pb-6">
                  <ControlledToggle control={control} name="covidFriendly" label="Is the building COVID friendly?" />
                </div>
                <div className="sm:grid space-y-8 sm:space-y-0 sm:grid-cols-6 gap-8 py-8 border-t border-zinc-300  dark:border-zinc-700 ">
                  <div className="col-span-2 flex space-x-4 items-start">
                    <div className="rounded-full p-2.5 bg-lime-200 dark:shadow-subtle shadow-subtler">
                      <ReactSVG
                        src="/icons/accessibility.svg"
                        title="accessibility"
                        className="dark:stroke-brand-500 stroke-lime-800 h-8 w-8"
                      />
                    </div>
                    <ControlledToggle
                      control={control}
                      name="accessibility"
                      label="Is the unit and building accessibility friendly?"
                      stacked
                    />
                  </div>
                  <ControlledTextarea
                    name="accessibilityStory"
                    control={control}
                    label="Share some details on the elevators, stairs and more."
                    description="Come as you are."
                    className="col-span-4"
                  />
                </div>
                <div className="sm:grid space-y-8 sm:space-y-0 sm:grid-cols-6 gap-8 py-8 border-t border-zinc-300  dark:border-zinc-700 ">
                  <div className="col-span-2 flex space-x-4 items-start">
                    <div className="rounded-full p-2.5 bg-yellow-200 dark:shadow-subtle shadow-subtler">
                      <ReactSVG src="/icons/pets.svg" title="Pets" className="stroke-yellow-800 h-8 w-8" />
                    </div>

                    <ControlledToggle control={control} name="petsWelcome" label="Are Pets welcome?" stacked />
                  </div>

                  <ControlledTextarea
                    name="petsWelcomeStory"
                    control={control}
                    label="Tell us more about how pets are treated in the rental"
                    className="col-span-4"
                    // description="Cats and Landlords don't always get along."
                  />
                </div>

                <div className="sm:grid space-y-8 sm:space-y-0 sm:grid-cols-6 gap-8 py-8 border-t border-zinc-300  dark:border-zinc-700 ">
                  <div className="col-span-2 flex space-x-4 items-start">
                    <div className="rounded-full p-2.5 bg-rose-200 dark:shadow-subtle shadow-subtler">
                      <ReactSVG src="/icons/languages.svg" title="languages" className="stroke-rose-800 h-8 w-8" />
                    </div>

                    <ControlledToggle
                      control={control}
                      name="landlordLanguages"
                      label="Do you speak any languages other than English?"
                      className="col-span-2"
                      stacked
                    />
                  </div>

                  <ControlledTextarea
                    name="landlordLanguagesStory"
                    control={control}
                    label="Tell us what languages you're certain they spoke"
                    className="col-span-4"
                  />
                </div>
                <div className="flex justify-end mt-6">
                  <Button outline small type="button" onClick={() => handleChange(selectedIndex + 1)}>
                    Next
                  </Button>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3">
                <div className="mb-6">
                  <Title as="h3" size="lg">
                    Images
                  </Title>
                  <p className="mt-2 max-w-2xl text-sm dark:text-zinc-200 text-zinc-600 font-medium">
                    Help us provide more information to tenants
                  </p>
                </div>
                <ImageUploader handleUpdate={handleImages} data={images} />
              </Tab.Panel>

              <div className="space-x-4 flex items-center justify-end border-t border-zinc-300  dark:border-zinc-700  pt-16 mt-16">
                {canGoBack && (
                  <ButtonLink type="button" onClick={handleBack}>
                    Back
                  </ButtonLink>
                )}
                {Object.keys(errors).length > 0 ? (
                  <div className="max-w-lg flex items-start space-x-2 relative">
                    <XCircleIcon className="h-6 w-6 text-rose-500 absolute -left-6 top-0" aria-hidden="true" />
                    <p className="text-sm text-rose-500">
                      {Object.values(errors)
                        .map(({ message }) => message)
                        .join(', ')}{' '}
                      are missing
                    </p>
                  </div>
                ) : null}
                {selectedIndex === 4 ? <Button type="submit">Submit</Button> : null}
              </div>
            </Tab.Panels>
          </form>
        </Tab.Group>
      </div>
    </>
  )
}
